import React, { useEffect, useMemo, useState } from "react";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {
  ExternalLink,
  MessageSquareMore,
  Reply,
  Images,
  History,
  Filter,
  X,
  Star,
  Calendar,
  Globe,
  Info,
  Copy,
  Check,
  LoaderCircle,
} from "lucide-react";
import { baseUrl } from "../../../Environments/environment";
import { apiClient } from "../../../api";
import google from "../../../Assets/google.png";
import onelyk from "../../../Assets/onelyk.png";
import yelp from "../../../Assets/yelp.png";
import tripadvisor from "../../../Assets/tripadvisor.png";
import booking from "../../../Assets/booking.png";
import expedia from "../../../Assets/expedia.png";
import agoda from "../../../Assets/agoda.png";
import { toast } from "react-toastify";
import ReviewModal from "../../ReviewModal/ReviewModal";

const RepliesModal = ({ replies, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-xl rounded-lg shadow-xl p-4 sm:p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base sm:text-lg font-semibold">Replies</h3>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 p-1"
            aria-label="Close replies"
          >
            ✕
          </button>
        </div>
        {(!replies || replies.length === 0) && (
          <div className="text-sm text-gray-500">No replies yet.</div>
        )}
        <div className="space-y-4">
          {replies?.map((rep, idx) => (
            <div key={rep?.id || idx} className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-gray-800">
                  {rep?.author?.name}
                </div>
                <div className="text-xs text-gray-500">
                  {rep?.timestamp
                    ? new Date(rep.timestamp).toLocaleString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                    : ""}
                </div>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-wrap">
                {rep?.text || "~ no text ~"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const renderStars = (rating) => {
  const full = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <div className="flex items-center gap-1">
      {[...Array(5)].map((_, i) => (
        <span
          key={i}
          className={
            i < full ? "text-yellow-400 text-xl" : "text-gray-300 text-xl"
          }
        >
          ★
        </span>
      ))}
    </div>
  );
};

// ...existing code...

// Add this modal component above your Reviews component
const ConfirmAcknowledgeModal = ({ open, review, onConfirm, onCancel }) => {
  if (!open || !review) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-2">
          Acknowledge Review?
        </h3>
        <div className="mb-4 text-sm sm:text-base text-gray-700">
          Are you sure you made a response to this review?
          <br />
          <span className="font-semibold">Reviewer:</span> {review.name}
          <br />
          <span className="font-semibold">Review:</span>{" "}
          {(review.comments ?? review.text ?? "").slice(0, 120)}...
        </div>
        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
          <button
            onClick={onCancel}
            className="px-3 sm:px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-sm sm:text-base"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-3 sm:px-4 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700 text-sm sm:text-base"
          >
            Yes, I responded
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Reviews() {
  const { selectedPropertyId } = useProperty();
  const [isLoading, setIsLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [selectedReview, setSelectedReview] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [repliesForModal, setRepliesForModal] = useState(null);
  const [photosForModal, setPhotosForModal] = useState(null);
  const [editsForModal, setEditsForModal] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalNotRepliedReviews, setTotalNotRepliedReviews] = useState(0);
  const [totalRepliedReviews, setTotalRepliedReviews] = useState(0);
  // Platform state
  const [selectedPlatform, setSelectedPlatform] = useState("google");
  const [platforms, setPlatforms] = useState([]);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState(true);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    hasPhotos: false,
    hasReplies: false,
    hasNoReplies: false,
    hasEdits: false,
    isHidden: false,
    isRecommended: false,
    isDeleted: false,
    includeDeleted: false,
    maxRating: "",
    minRating: "",
    postedBefore: "",
    postedAfter: "",
    // language: "",
    sortBy: "timestamp",
    sortDirection: "DESC",
  });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    review: null,
  });
  const [generatedReplyModal, setGeneratedReplyModal] = useState({
    open: false,
    review: null,
    replyText: "",
    loading: false,
  });
  // Action needed filter state
  const [showOnlyActionNeeded, setShowOnlyActionNeeded] = useState(false);

  // Fetch platforms from API
  const fetchPlatforms = async () => {
    try {
      setIsLoadingPlatforms(true);
      const token = localStorage.getItem("accessToken");
      const response = await apiClient.get(
        `${baseUrl}/reviews/jobs/${selectedPropertyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const platformsList = response?.data?.result?.reviewJobs || [];
      platformsList.push({
        network: "onelyk",
      });
      setPlatforms(platformsList);

      // Set the first platform as default if platforms exist and no platform is currently selected
      if (platformsList.length > 0 && !selectedPlatform) {
        setSelectedPlatform("google");
      }
    } catch (error) {
      console.error("Error fetching platforms", error);
      // Fallback to default platforms if API fails
      // const defaultPlatforms = [
      //   { network: "google", jobId: "37a11297-ec9a-4e0d-97ef-0bfb3e349935" },
      //   {
      //     network: "tripadvisor",
      //     jobId: "2af2fb7b-2a68-45eb-9fdb-b286d46ee444",
      //   },
      // ];
      // setPlatforms(defaultPlatforms);
      // if (!selectedPlatform) {
      //   setSelectedPlatform(defaultPlatforms[0].network);
      // }
    } finally {
      setIsLoadingPlatforms(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("accessToken");
      const offset = (page - 1) * limit;

      // Build params object with filters
      const params = { offset, limit };

      // Add platform parameters if a platform is selected
      if (selectedPlatform) {
        const platform = platforms.find((p) => p.network === selectedPlatform);
        if (platform) {
          params.network = platform.network;
          params.uid = platform.uid;
        }
      }

      // Add active filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== "" && value !== false) {
          if (key === "postedBefore" || key === "postedAfter") {
            params[key] = value ? new Date(value).toISOString() : "";
          } else {
            params[key] = value;
          }
        }
      });

      const response = await apiClient.get(
        `${baseUrl}/reviews/getAll/${selectedPropertyId}`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const list =
        response?.data?.result?.reviews || response?.data?.reviews || [];
      const totalCount =
        response?.data?.result?.total || response?.data?.total || list.length;
      setTotal(Number(totalCount) || 0);
      setTotalNotRepliedReviews(
        Number(response?.data?.result?.totalNotRepliedReviews) || 0
      );
      setTotalRepliedReviews(
        Number(response?.data?.result?.totalRepliedReviews) || 0
      );
      setReviews(list);

      // Reset to page 1 if current page exceeds max pages after data change
      const maxPage = Math.ceil(Number(totalCount) / limit);
      if (page > maxPage && maxPage > 0) {
        setPage(1);
      }
    } catch (error) {
      console.error("Error fetching reviews", error);
      setReviews([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch platforms when component mounts
  useEffect(() => {
    fetchPlatforms();
  }, []);

  // Fetch reviews when platforms are loaded and platform is selected
  useEffect(() => {
    if (platforms.length > 0 && selectedPlatform) {
      fetchReviews();
    }
  }, [selectedPropertyId, page, limit, filters, selectedPlatform, platforms]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page when filters change
  };

  const handlePlatformChange = (e) => {
    setSelectedPlatform(e.target.value);
    clearAllFilters();
    setPage(1); // Reset to first page when platform changes
  };

  const clearAllFilters = () => {
    setFilters({
      hasPhotos: false,
      hasReplies: false,
      hasNoReplies: false,
      hasEdits: false,
      isHidden: false,
      isRecommended: false,
      isDeleted: false,
      includeDeleted: false,
      maxRating: "",
      minRating: "",
      postedBefore: "",
      postedAfter: "",
      // language: "",
      sortBy: "timestamp",
      sortDirection: "DESC",
    });
    // Don't clear platform selection - keep the first platform selected
    setPage(1);
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value) =>
      value !== "" &&
      value !== false &&
      value !== "timestamp" &&
      value !== "DESC"
  ).length; // Remove platform from active filters count since it's always selected

  const formattedReviews = useMemo(() => {
    return reviews.map((r) => {
      const nameFromGuest = `${r?.guestId?.firstName || ""} ${
        r?.guestId?.lastName || ""
      }`.trim();
      const nameFromAuthor = r?.author?.name || "";
      const name = (nameFromGuest || nameFromAuthor || "Guest").trim();
      const firstForInitial =
        r?.guestId?.firstName || name?.split(" ")[0] || "?";
      const lastForInitial = r?.guestId?.lastName || name?.split(" ")[1] || "?";
      const initials = `${firstForInitial.charAt(0)}${lastForInitial.charAt(
        0
      )}`.toUpperCase();
      const dateStr = r?.timestamp || r?.createdAt;
      const date = dateStr ? new Date(dateStr) : null;
      const displayDate = date
        ? date.toLocaleString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
        : "";
      return { ...r, name, initials, displayDate };
    });
  }, [reviews]);

  // Filter reviews based on action needed
  const filteredReviews = useMemo(() => {
    if (showOnlyActionNeeded) {
      return formattedReviews.filter(
        (review) => !review.replies || review.replies.length === 0
      );
    }
    return formattedReviews;
  }, [formattedReviews, showOnlyActionNeeded]);

  // Handle navigation to review platform
  const handleNavigateToReview = (review) => {
    if (review?.replyUrl) {
      window.open(review.replyUrl, "_blank");
    } else if (review?.url) {
      window.open(review.url, "_blank");
    }
    setConfirmModal({ open: true, review });
  };
  const handleConfirmAcknowledge = async () => {
    const review = confirmModal.review;
    setConfirmModal({ open: false, review: null });
    await acknowledgeReview(review);
  };
  const handleReplyReviewOnelyk = async (isClose) => {
    setIsReviewModalOpen(false);
    setSelectedReview(null);
    if (isClose) {
      await fetchReviews();
    }
  };
  const acknowledgeReview = async (review) => {
    try {
      console.log(review);
      const token = localStorage.getItem("accessToken");
      // Replace with your actual endpoint and payload
      const savedReply = await apiClient.post(
        `${baseUrl}/reviews/acknowledge/${selectedPropertyId}/${review.id}`,
        { ...review, network: selectedPlatform },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (savedReply.status == 201) {
        fetchReviews();
        toast.success("Review Mark as Read Successfully");
      } else {
        toast.error("Error while updating review status");
      }
    } catch (err) {
      console.error("Failed to acknowledge review", err);
      toast.error("Error while updating review status");
    }
  };
 
  const generateReply = async (review) => {
    try {
      setGeneratedReplyModal({
        open: true,
        review,
        replyText: "",
        loading: true,
      });
  
      const token = localStorage.getItem("accessToken");
  
      const response = await apiClient.post(
        `${baseUrl}/reviews/generateSeoReply/${selectedPropertyId}`,
        { review: { ...review, platform: selectedPlatform } },
        { headers: { Authorization: `Bearer ${token}` } }
      );
  console.log(response);
      if (response.status === 200) {
        const replyText = response?.data?.result?.reply || "";
  
        setGeneratedReplyModal({
          open: true,
          review,
          replyText,
          loading: false,
        });
  
        toast.success("Reply Generated Successfully");
      } else {
        toast.error("Error while generating reply");
        setGeneratedReplyModal({
          open: false,
          review: null,
          replyText: "",
          loading: false,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Error while generating reply");
      setGeneratedReplyModal({
        open: false,
        review: null,
        replyText: "",
        loading: false,
      });
    }
  };
  const handleFinalReply = (review) => {
    setGeneratedReplyModal({
      open: false,
      review: null,
      replyText: "",
      loading: false,
    });
  
    // OLD FLOW
    handleNavigateToReview(review);
  };
  const GeneratedReplyModal = ({ data, onClose, onReply }) => {
    const [copied, setCopied] = useState(false);
    const isOpen = Boolean(data?.open);

    if (!isOpen) return null;

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(data.replyText);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1200);
      } catch {
        toast.error("Copy failed");
      }
    };
  
    const reviewText = (data?.review?.comments ?? data?.review?.text ?? "").trim();
    const ratingValue = Number(
      data?.review?.overAllRating ?? data?.review?.rating ?? 0
    );

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden">
          <div className="flex items-start justify-between gap-4 px-4 sm:px-6 py-4 border-b bg-gradient-to-r from-indigo-50 to-white">
            <div className="min-w-0">
              <h3 className="font-semibold text-lg text-gray-900">
                Generated Reply
              </h3>
              <div className="mt-0.5 text-xs sm:text-sm text-gray-600 truncate">
                Review details and suggested reply
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-white/70 transition-colors"
              aria-label="Close generated reply modal"
            >
              <X size={18} />
            </button>
          </div>
  
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Review details */}
            <div className="px-4 sm:px-6 py-4 border-b lg:border-b-0 lg:border-r bg-white">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900 truncate">
                    {data?.review?.name || "Guest"}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1">
                      <Star size={14} className="text-yellow-500" />
                      <span className="font-medium text-gray-700">
                        {Number.isFinite(ratingValue) ? ratingValue : 0}
                      </span>
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{data?.review?.displayDate || "—"}</span>
                    </span>
                    <span className="text-gray-300">•</span>
                    <span className="inline-flex items-center gap-1">
                      <Globe size={14} />
                      <span className="capitalize">{selectedPlatform}</span>
                    </span>
                  </div>
                  <div className="mt-2">{renderStars(ratingValue)}</div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {data?.review?.url && (
                    <a
                      href={data.review.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
                    >
                      <ExternalLink size={14} />
                      View
                    </a>
                  )}
                
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs font-semibold text-gray-700 mb-2">
                  Review
                </div>
                <div className="border border-gray-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-[240px] overflow-y-auto bg-gray-50/40">
                  {reviewText || "—"}
                </div>
              </div>
            </div>

            {/* Generated reply */}
            <div className="px-4 sm:px-6 py-4 bg-white">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-gray-700">
                  Suggested reply
                </div>
                {!data.loading && (
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center justify-center p-2 rounded-md border border-gray-200 hover:bg-gray-50 text-gray-700"
                    aria-label="Copy generated reply"
                    title={copied ? "Copied" : "Copy"}
                  >
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </button>
                )}
              </div>

              {data.loading ? (
                <div className="mt-3 border border-gray-200 rounded-lg p-4 bg-gray-50/40">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <LoaderCircle size={18} className="animate-spin" />
                    Generating reply…
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-11/12 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-10/12 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-9/12 animate-pulse" />
                    <div className="h-3 bg-gray-200 rounded w-7/12 animate-pulse" />
                  </div>
                </div>
              ) : (
                <div className="mt-3 border border-gray-200 rounded-lg p-3 text-sm text-gray-800 whitespace-pre-wrap max-h-[320px] overflow-y-auto bg-gray-50/40 relative">
                  {data.replyText || "No reply generated"}
                </div>
              )}

              <div className="mt-4 flex flex-col sm:flex-row justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => onReply(data.review)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={data.loading}
                >
                  Reply to Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {isLoading && <LoadingSpinner />}

      {/* Filter Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Reviews
          </h2>
          {activeFiltersCount > 0 && (
            <span className="bg-indigo-100 text-indigo-800 text-xs sm:text-sm font-medium px-2 sm:px-2.5 py-0.5 rounded-full">
              {activeFiltersCount} filter{activeFiltersCount !== 1 ? "s" : ""}{" "}
              active
            </span>
          )}
        </div>
        <div className="flex flex-row items-center gap-2 sm:gap-3 w-full">
          {/* Platform Dropdown */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap flex-shrink-0">
              Platform:
            </label>
            {isLoadingPlatforms ? (
              <div className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm bg-gray-50 text-gray-500 flex-1 min-w-0">
                Loading...
              </div>
            ) : (
              <select
                value={selectedPlatform}
                onChange={handlePlatformChange}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-md text-xs sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white flex-1 min-w-0"
              >
                {platforms.map((platform) => (
                  <option key={platform.network} value={platform.network}>
                    {platform.network.charAt(0).toUpperCase() +
                      platform.network.slice(1)}
                  </option>
                ))}
              </select>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
          >
            <Filter size={14} className="sm:w-4 sm:h-4" />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-indigo-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Filter Sidebar - Bottom Sheet on mobile, Sidebar on desktop */}
        {showFilters && (
          <>
            {/* Overlay */}
            <div
              className={`fixed inset-0 bg-black transition-opacity duration-300 z-[100] ${
                showFilters ? "opacity-50" : "opacity-0 pointer-events-none"
              }`}
              onClick={() => setShowFilters(false)}
            />
            
            {/* Filter Panel (Bottom Sheet on Mobile, Sticky Sidebar on Desktop) */}
            <div className={`
              fixed bottom-0 left-0 right-0 z-[101] w-full bg-white rounded-t-2xl p-4 sm:p-6 shadow-2xl transition-transform duration-300 transform ease-in-out lg:block lg:z-auto lg:w-80 lg:h-fit lg:rounded-lg lg:border lg:border-gray-200 lg:sticky lg:top-4 lg:shadow-none lg:translate-y-0
              ${showFilters ? "translate-y-0" : "translate-y-full"}
              max-h-[85vh] overflow-y-auto lg:max-h-none lg:overflow-visible
            `}>
              {/* Bottom Sheet Handle (Mobile Only) */}
              <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4 lg:hidden" />
              
              <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 lg:relative lg:pb-0 z-10">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Filters
                </h3>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 bg-gray-100 rounded-full lg:bg-transparent"
                  aria-label="Close filters"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Clear All Filters */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="w-full mb-4 px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors sticky top-12 bg-white lg:static lg:top-auto z-10"
                >
                  Clear all filters
                </button>
              )}

              {/* Filter Content */}
              <div className="pb-10 lg:pb-0">

              {/* Platform Selection in Sidebar */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-3">Platform</h4>
                {isLoadingPlatforms ? (
                  <div className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-50 text-gray-500">
                    Loading...
                  </div>
                ) : (
                  <select
                    value={selectedPlatform}
                    onChange={handlePlatformChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    {platforms.map((platform) => (
                      <option key={platform.network} value={platform.network}>
                        {platform.network.charAt(0).toUpperCase() +
                          platform.network.slice(1)}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Review Content Filters */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Review Content
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.hasPhotos}
                        onChange={(e) =>
                          handleFilterChange("hasPhotos", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Has Photos</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.hasReplies}
                        onChange={(e) =>
                          handleFilterChange("hasReplies", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Has Replies</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.hasEdits}
                        onChange={(e) =>
                          handleFilterChange("hasEdits", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Has Edits</span>
                    </label>
                  </div>
                </div>

                {/* Review Status */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Review Status
                  </h4>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.isHidden}
                        onChange={(e) =>
                          handleFilterChange("isHidden", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">
                        Hidden Reviews
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.isRecommended}
                        onChange={(e) =>
                          handleFilterChange("isRecommended", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">Recommended</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.isDeleted}
                        onChange={(e) =>
                          handleFilterChange("isDeleted", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">
                        Deleted Reviews
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.includeDeleted}
                        onChange={(e) =>
                          handleFilterChange("includeDeleted", e.target.checked)
                        }
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">
                        Include Deleted
                      </span>
                    </label>
                  </div>
                </div>

                {/* Rating Range */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Star size={16} />
                    Rating Range
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Minimum Rating
                      </label>
                      <select
                        value={filters.minRating}
                        onChange={(e) =>
                          handleFilterChange("minRating", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Any rating</option>

                        <option value="2">2 stars</option>
                        <option value="3">3 stars</option>
                        <option value="4">4 stars</option>
                        <option value="5">5 stars</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Maximum Rating
                      </label>
                      <select
                        value={filters.maxRating}
                        onChange={(e) =>
                          handleFilterChange("maxRating", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Any rating</option>
                        <option value="1">1 star</option>
                        <option value="2">2 stars</option>
                        <option value="3">3 stars</option>
                        <option value="4">4 stars</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar size={16} />
                    Date Range
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Posted After
                      </label>
                      <input
                        type="date"
                        value={filters.postedAfter}
                        onChange={(e) =>
                          handleFilterChange("postedAfter", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Posted Before
                      </label>
                      <input
                        type="date"
                        value={filters.postedBefore}
                        onChange={(e) =>
                          handleFilterChange("postedBefore", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Language */}
                {/* <div>
                <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <Globe size={16} />
                  Language
                </h4>
                <input
                  type="text"
                  placeholder="Enter language code (e.g., en, es, fr)"
                  value={filters.language}
                  onChange={(e) =>
                    handleFilterChange("language", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div> */}

                {/* Sort Options */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Sort By</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Sort Field
                      </label>
                      <select
                        value={filters.sortBy}
                        onChange={(e) =>
                          handleFilterChange("sortBy", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="timestamp">Publish Time</option>
                        <option value="rating">Rating</option>
                        <option value="recommendation">Recommendation</option>
                        <option value="hidden">Hidden</option>
                        <option value="text">Text</option>
                        <option value="id">Review ID</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Sort Direction
                      </label>
                      <select
                        value={filters.sortDirection}
                        onChange={(e) =>
                          handleFilterChange("sortDirection", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="DESC">Newest First</option>
                        <option value="ASC">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

        {/* Reviews Grid */}
        <div
          className={`${showFilters ? "flex-1 w-full lg:w-auto" : "w-full"}`}
        >
          {/* Reviews Summary */}
          {(selectedPlatform === "tripadvisor" ||
            selectedPlatform === "google" ||
            selectedPlatform === "onelyk" ||
            selectedPlatform === "booking") && (
            <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto justify-between sm:justify-start">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-gray-900">
                      {total}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500">
                      Total Reviews
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-red-600">
                      {totalNotRepliedReviews}
                    </div>
                    <div className="text-xs sm:text-sm text-red-500">
                      Need Action
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-green-600">
                      {totalRepliedReviews}
                    </div>
                    <div className="text-xs sm:text-sm text-green-500">
                      Replied
                    </div>
                  </div>
                </div>

                {/* Filter Toggle */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-xs sm:text-sm font-medium text-gray-700 whitespace-normal sm:whitespace-nowrap">
                    Show only reviews needing action:
                  </label>
                  <input
                    type="checkbox"
                    checked={filters.hasNoReplies}
                    onChange={(e) =>
                      handleFilterChange("hasNoReplies", e.target.checked)
                    }
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded flex-shrink-0"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4">
            {filteredReviews.map((review, index) => (
              <div
                key={review?._id || index}
                className="bg-white shadow-sm border border-gray-100 rounded-lg p-3 sm:p-4 md:p-6 transition-all hover:shadow-md transform hover:-translate-y-0.5 duration-200 flex flex-col h-full"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 ">
                    {/* Author Image */}
                    {review?.author?.photo ? (
                      <div className="relative">
                        <img
                          src={review?.author?.photo}
                          alt={review.name || "Author"}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                        <img
                          src={
                            selectedPlatform === "google"
                              ? google
                              : selectedPlatform === "tripadvisor"
                              ? tripadvisor
                              : selectedPlatform === "booking"
                              ? booking
                              : selectedPlatform === "agoda"
                              ? agoda
                              : selectedPlatform === "expedia"
                              ? expedia
                              : selectedPlatform === "yelp"
                              ? yelp
                              : selectedPlatform === "onelyk"
                              ? onelyk
                              : null
                          }
                          alt="Platform"
                          className="h-5 w-5 rounded-full border-1 border-white absolute -bottom-1 -right-1"
                        />
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-400 text-white font-medium">
                          {review.initials}
                        </div>
                        <img
                          src={
                            selectedPlatform === "google"
                              ? google
                              : selectedPlatform === "tripadvisor"
                              ? tripadvisor
                              : selectedPlatform === "booking"
                              ? booking
                              : selectedPlatform === "agoda"
                              ? agoda
                              : selectedPlatform === "expedia"
                              ? expedia
                              : selectedPlatform === "yelp"
                              ? yelp
                              : selectedPlatform === "onelyk"
                              ? onelyk
                              : null
                          } // small platform icon URL
                          alt="Platform"
                          className="h-5 w-5 rounded-full border-1 border-white absolute -bottom-1 -right-1"
                        />
                      </div>
                    )}

                    <div className="ml-2">
                      {review?.author?.url ? (
                        <a
                          href={review.author.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm sm:text-base font-medium text-indigo-600 hover:underline"
                        >
                          {review.name || "Guest"}
                        </a>
                      ) : (
                        <div className="text-sm sm:text-base font-medium text-gray-900">
                          {review.name || "Guest"}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-700 font-semibold ">
                        {Number(review.overAllRating ?? review.rating ?? 0)}
                      </span>
                      {renderStars(review.overAllRating ?? review.rating)}
                    </div>
                    {/* {review?.guestStatus?.currentStatus && (
                      <div className="flex items-center justify-end gap-2">
                        <span className="inline-flex items-center  p-1 rounded-full text-xs font-small bg-blue-100 text-blue-500 text-center">
                          {review?.guestStatus?.currentStatus}
                        </span>
                      </div>
                    )} */}
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 text-gray-700 leading-relaxed">
                  {(() => {
                    const reviewText = review?.comments ?? review?.text ?? "";
                    const trimmedText = reviewText.trim();
                    const isLongText = trimmedText.length > 300;
                    const isExpanded = expandedReviews.has(
                      review?._id || index
                    );

                    if (!trimmedText) {
                      return (
                        <p className="italic text-gray-400">~ no text ~</p>
                      );
                    }

                    if (!isLongText) {
                      return <p>{trimmedText}</p>;
                    }

                    const displayText = isExpanded
                      ? trimmedText
                      : trimmedText.substring(0, 300);

                    return (
                      <div>
                        <p>
                          {displayText}
                          {!isExpanded && "..."}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const reviewId = review?._id || index;
                            setExpandedReviews((prev) => {
                              const newSet = new Set(prev);
                              if (newSet.has(reviewId)) {
                                newSet.delete(reviewId);
                              } else {
                                newSet.add(reviewId);
                              }
                              return newSet;
                            });
                          }}
                          className="text-indigo-600 hover:text-indigo-800 text-sm font-medium mt-1"
                        >
                          {isExpanded ? "Read less" : "Read more"}
                        </button>
                      </div>
                    );
                  })()}
                </div>

                <div className="mt-auto pt-4 space-y-3 text-sm text-gray-500">
                  {/* Row 1: Actions & Status */}
                  <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
                    <button
                      className="flex items-center gap-1 hover:text-indigo-600"
                      onClick={() => setRepliesForModal(review.replies || [])}
                      title="Show replies"
                    >
                      <MessageSquareMore size={16} />
                      <span>{review?.replies?.length || 0}</span>
                    </button>

                    {/* Action Needed Indicator - Show for reviews without replies */}
                    {(!review?.replies || review.replies.length === 0) &&
                      (selectedPlatform === "tripadvisor" ||
                        selectedPlatform === "google" ||
                        selectedPlatform === "onelyk" ||
                        selectedPlatform === "booking") &&
                      (review.isAcknowledged ? (
                        <div className="relative group cursor-pointer">
                          <div className=" inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 text-center">
                            <Info size={14} className="mr-1" /> Status Pending
                          </div>
                          <div className="absolute left-0 top-full mt-1 w-max max-w-xs bg-gray-800 text-white text-xs rounded px-2 py-1 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-normal shadow-lg">
                            Review is mark as replied, soon will be update
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="relative group cursor-pointer">
                            <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 text-center">
                              <Info size={14} className="mr-1" /> Action Needed
                            </div>
                            <div className="absolute left-0 top-full mt-1 w-max max-w-xs bg-gray-800 text-white text-xs rounded px-2 py-1 z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-normal shadow-lg">
                              Review is not replied yet !
                            </div>
                          </div>
                          {selectedPlatform === "onelyk" ? (
                            <button
                              onClick={() => {
                                setIsReviewModalOpen(true);
                                setSelectedReview(review);
                              }}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
                              title="Reply to this review"
                            >
                              <Reply size={16} />
                              <span>Reply </span>
                            </button>
                          ) : (
                            <button
                            onClick={() => generateReply(review)}
                              className="flex items-center gap-1 text-red-600 hover:text-red-800 font-medium"
                              title="Reply to this review"
                            >
                              <Reply size={16} />
                              <span>Reply </span>
                            </button>
                          )}         
                        </div>
                      ))}
                  </div>

                  {/* Row 2: View Actions & Date */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-50 flex-wrap gap-y-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {review?.url && (
                        <a
                          href={review.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800 transition-colors"
                          title="Open review link"
                        >
                          <ExternalLink size={14} />
                          <span className="text-xs font-medium">Original</span>
                        </a>
                      )}

                      {Array.isArray(review?.edits) && review.edits.length > 0 && (
                        <button
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 transition-colors"
                          onClick={() => setEditsForModal(review.edits)}
                        >
                          <History size={14} /> Edits ({review.edits.length})
                        </button>
                      )}

                      {Array.isArray(review?.photos) && review.photos.length > 0 && (
                        <button
                          className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 transition-colors"
                          onClick={() => setPhotosForModal(review.photos)}
                        >
                          <Images size={14} /> Photos ({review.photos.length})
                        </button>
                      )}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400 ml-auto pt-1 sm:pt-0">
                      {review.displayDate}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Spacer to avoid bottom sheet overlapping last items on mobile */}
      <div className="h-20 lg:hidden" />

      {Array.isArray(repliesForModal) && (
        <RepliesModal
          replies={repliesForModal}
          onClose={() => setRepliesForModal(null)}
        />
      )}
      {Array.isArray(photosForModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white w-full max-w-3xl rounded-lg shadow-xl p-4 sm:p-5 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold">Photos</h3>
              <button
                onClick={() => setPhotosForModal(null)}
                className="text-gray-600 hover:text-gray-900 p-1"
                aria-label="Close photos"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              {photosForModal.map((p, idx) => {
                const src =
                  typeof p === "string" ? p : p?.url || p?.photo || "";
                return (
                  <a
                    key={idx}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    <img
                      src={src}
                      className="w-full h-40 object-cover rounded-md"
                    />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {Array.isArray(editsForModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-lg shadow-xl p-4 sm:p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-semibold">Edits</h3>
              <button
                onClick={() => setEditsForModal(null)}
                className="text-gray-600 hover:text-gray-900 p-1"
                aria-label="Close edits"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              {editsForModal.map((e, idx) => {
                const text =
                  typeof e === "string" ? e : e?.text || e?.content || "";
                const ts =
                  typeof e === "object" && e?.timestamp
                    ? e.timestamp
                    : undefined;
                return (
                  <div key={idx} className="border rounded-md p-3">
                    {ts && (
                      <div className="text-xs text-gray-500 mb-1">
                        {new Date(ts).toLocaleString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                    <div className="text-sm text-gray-700 whitespace-pre-wrap">
                      {text || "~ no text ~"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
        <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
          {(() => {
            const start = total === 0 ? 0 : (page - 1) * limit + 1;
            const end = Math.min(page * limit, total);
            return `Showing ${start}-${end} of ${total}`;
          })()}
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto order-1 sm:order-2">
          <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start">
            <label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
              Rows per page
            </label>
            <select
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);
                setLimit(newLimit);
                setPage(1);
              }}
              className="border rounded-md pl-2 pr-8 py-1 text-xs sm:text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
              {[5, 10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <button
              className="px-2 sm:px-3 py-1 border rounded-md text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-50 disabled:hover:bg-transparent"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <div className="text-xs sm:text-sm text-gray-700 whitespace-nowrap">
              Page {page} of {Math.max(1, Math.ceil(total / limit))}
            </div>
            <button
              className="px-2 sm:px-3 py-1 border rounded-md text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-50 disabled:hover:bg-transparent"
              onClick={() => {
                const maxPage = Math.max(1, Math.ceil(total / limit));
                setPage((p) => Math.min(maxPage, p + 1));
              }}
              disabled={page >= Math.ceil(total / limit) || total === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {selectedReview && (
        <ReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => handleReplyReviewOnelyk(true)}
          onCancel={() => handleReplyReviewOnelyk(false)}
          review={selectedReview}
        />
      )}
      <ConfirmAcknowledgeModal
        open={confirmModal.open}
        review={confirmModal.review}
        onConfirm={handleConfirmAcknowledge}
        onCancel={() => setConfirmModal({ open: false, review: null })}
      />
      <GeneratedReplyModal
  data={generatedReplyModal}
  onClose={() =>
    setGeneratedReplyModal({
      open: false,
      review: null,
      replyText: "",
      loading: false,
    })
  }
  onReply={handleFinalReply}
/>
    </div>
  );
}
